import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import {
	fetchTablePref,
	saveTablePref,
	deleteTablePref,
} from '../redux/actions/tablePrefs';

/**
 * Persistencia hibrida de preferencias de tabla.
 *
 * - sessionStorage: cache local para render inmediato (clave `tablePrefs:<tableKey>:<userId>`).
 * - backend: fuente de verdad cross-dispositivo (POST/GET/DELETE en /users/:userId/table-prefs/:tableKey).
 *
 * Ciclo:
 * 1. Hidratacion sincrona desde sessionStorage o `defaults` para evitar flicker.
 * 2. Al montar (o cambiar `userId`/`tableKey`) se hace fetch al backend; si trae prefs,
 *    sustituye estado y sessionStorage. Si el usuario ya empezo a editar antes de que
 *    responda, se ignora la hidratacion para no pisar sus cambios.
 * 3. Cada cambio escribe sessionStorage al instante y agenda un PUT debounced (~500 ms)
 *    al backend. Al desmontar se hace flush inmediato si hay pendiente.
 * 4. `resetPrefs` vuelve a `defaults`, limpia sessionStorage y dispara DELETE remoto.
 *
 * Tolerancia a fallos:
 * - Si fetch o save fallan, no se rompe la pagina; el siguiente cambio reintenta el PUT.
 * - Si no hay sessionStorage (Safari privado, SSR), se opera en memoria.
 *
 * @template T
 * @param {string} tableKey identificador estable (ej. 'alumnos').
 * @param {T} defaults shape completo de los prefs (debe ser estable entre renders).
 * @returns {[T, (updater: T | ((prev: T) => T)) => void, (patch: Partial<T>) => void, () => void, boolean]}
 * El quinto valor (`prefsHydrated`) es false mientras se espera el primer GET remoto (si hay `userId`);
 * sirve para mostrar skeleton sin parpadear la config por defecto. Sin usuario, queda true.
 */
const FLUSH_DELAY_MS = 500;

export const useTablePrefs = (tableKey, defaults) => {
	const userId = useSelector((state) => state?.user?.user?._id || null);
	const storageKey = buildStorageKey(tableKey, userId);

	const [prefs, setPrefsState] = useState(() => hydrate(storageKey, defaults));
	// Sin userId no hay fetch remoto: listo de inmediato. Con userId, esperar al primer GET.
	const [prefsHydrated, setPrefsHydrated] = useState(() => !userId);

	// Refs para coordinar hidratacion remota y flushing.
	const lastKeyRef = useRef(storageKey);
	const userTouchedSinceMountRef = useRef(false);
	const flushTimerRef = useRef(null);
	const pendingPrefsRef = useRef(null);
	const userIdRef = useRef(userId);
	const tableKeyRef = useRef(tableKey);

	useEffect(() => { userIdRef.current = userId; }, [userId]);
	useEffect(() => { tableKeyRef.current = tableKey; }, [tableKey]);

	const flushNow = useCallback(async () => {
		if (flushTimerRef.current) {
			clearTimeout(flushTimerRef.current);
			flushTimerRef.current = null;
		}
		const pending = pendingPrefsRef.current;
		pendingPrefsRef.current = null;
		const uid = userIdRef.current;
		const key = tableKeyRef.current;
		if (!pending || !uid || !key) return;
		await saveTablePref(uid, key, pending);
	}, []);

	const scheduleFlush = useCallback((next) => {
		pendingPrefsRef.current = next;
		if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
		flushTimerRef.current = setTimeout(() => {
			flushTimerRef.current = null;
			flushNow();
		}, FLUSH_DELAY_MS);
	}, [flushNow]);

	// Hidratacion: cambia el storageKey -> reset de estado local y fetch remoto.
	useEffect(() => {
		const keyChanged = lastKeyRef.current !== storageKey;
		if (keyChanged) {
			lastKeyRef.current = storageKey;
			userTouchedSinceMountRef.current = false;
			setPrefsState(hydrate(storageKey, defaults));
		}

		if (!userId) {
			setPrefsHydrated(true);
			return undefined;
		}

		setPrefsHydrated(false);
		let cancelled = false;
		fetchTablePref(userId, tableKey)
			.then((remote) => {
				if (cancelled) return;
				// Si el usuario ya hizo cambios mientras esperabamos, no pisamos.
				if (userTouchedSinceMountRef.current) return;
				if (!remote || !remote.prefs) return;
				const merged = deepMerge(defaults, remote.prefs);
				setPrefsState(merged);
				safeSet(storageKey, merged);
			})
			.finally(() => {
				if (!cancelled) setPrefsHydrated(true);
			});

		return () => {
			cancelled = true;
		};
	}, [storageKey, userId, tableKey, defaults]);

	const setPrefs = useCallback((updater) => {
		userTouchedSinceMountRef.current = true;
		setPrefsState((prev) => {
			const next = typeof updater === 'function' ? updater(prev) : updater;
			safeSet(storageKey, next);
			scheduleFlush(next);
			return next;
		});
	}, [storageKey, scheduleFlush]);

	const patchPrefs = useCallback((patch) => {
		setPrefs((prev) => ({ ...prev, ...patch }));
	}, [setPrefs]);

	const resetPrefs = useCallback(() => {
		userTouchedSinceMountRef.current = true;
		if (flushTimerRef.current) {
			clearTimeout(flushTimerRef.current);
			flushTimerRef.current = null;
		}
		pendingPrefsRef.current = null;
		setPrefsState(defaults);
		safeRemove(storageKey);
		if (userId) {
			deleteTablePref(userId, tableKey);
		}
	}, [storageKey, defaults, userId, tableKey]);

	// Flush pendiente al desmontar o al cambiar de usuario/tabla.
	useEffect(() => () => { flushNow(); }, [flushNow]);

	return [prefs, setPrefs, patchPrefs, resetPrefs, prefsHydrated];
};

const buildStorageKey = (tableKey, userId) =>
	`tablePrefs:${tableKey}:${userId || 'anonymous'}`;

const hydrate = (storageKey, defaults) => {
	const stored = safeGet(storageKey);
	return stored && typeof stored === 'object' ? deepMerge(defaults, stored) : defaults;
};

const isPlainObject = (value) =>
	Object.prototype.toString.call(value) === '[object Object]';

// Si llega una clave nueva en defaults (ej. al agregar features) se rellena con el default;
// las claves ya guardadas mantienen el valor del usuario.
const deepMerge = (base, overrides) => {
	if (!isPlainObject(base) || !isPlainObject(overrides)) return overrides;
	const out = { ...base };
	for (const key of Object.keys(overrides)) {
		const baseValue = base[key];
		const overrideValue = overrides[key];
		out[key] = isPlainObject(baseValue) && isPlainObject(overrideValue)
			? deepMerge(baseValue, overrideValue)
			: overrideValue;
	}
	return out;
};

const safeGet = (key) => {
	try {
		if (typeof window === 'undefined' || !window.sessionStorage) return null;
		const raw = window.sessionStorage.getItem(key);
		return raw ? JSON.parse(raw) : null;
	} catch {
		return null;
	}
};

const safeSet = (key, value) => {
	try {
		if (typeof window === 'undefined' || !window.sessionStorage) return;
		window.sessionStorage.setItem(key, JSON.stringify(value));
	} catch {
		// sessionStorage lleno o no disponible: degrada silenciosamente.
	}
};

const safeRemove = (key) => {
	try {
		if (typeof window === 'undefined' || !window.sessionStorage) return;
		window.sessionStorage.removeItem(key);
	} catch {
		// idem.
	}
};
